import React, { ChangeEvent, useEffect, useState } from "react";
import { Button, Dimmer, Form, FormButton, Grid, InputOnChangeData, Loader, Message, Modal, Progress } from "semantic-ui-react";
import SemanticDatepicker from "react-semantic-ui-datepickers";
import agent from "../../agent";
import {GeneralTitleCase, getGeneralTitle, getTZ} from "../../utils/common";
import BackupFileList from "./BackupFileList";
import BackupAutoSettings from "./BackupAutoSettings";
import EditableText from "../general/EditableText/EditableText";
import {hasRole, UserRole} from "../../helpers/UserRole";
import styles from "../workTableAppointments/index.module.css";
import {Descendant} from "slate";
import {htmlToSlate, isSlateContentEmpty, slateToHtml} from "../general/SlateEditableText/SlateHelpers";
import SlateEditableText from "../general/SlateEditableText/SlateEditableText";
import {EMPTY_SLATE_VALUE} from "../../consts/EventConsts";
import {UsefulInformationPanelOptions} from "./index";
import { textLabels } from "../../consts/textLabels";

const UsefulInformationConfig = () => {
    const [loading, setLoading] = useState(false);
    const [saved, setSaved] = useState(false);
    const [name, setName] = useState<Descendant[]>(EMPTY_SLATE_VALUE);
    const [originalName, setOriginalName] = useState<Descendant[]>(EMPTY_SLATE_VALUE);
    const [editable, setEditable] = useState(false);

    useEffect(() => {
        getUsefulInformation();
    }, []);

    const getUsefulInformation = async (): Promise<void> => {
        setLoading(true);
        try {
            const config = await agent.UsefulInformationProvider.get();
            if (config?.name) {
                const slateValue = htmlToSlate(config.name);

                if (Array.isArray(slateValue) && slateValue.length > 0) {
                    setName(slateValue);
                    setOriginalName(slateValue);
                } else {
                    setName(EMPTY_SLATE_VALUE);
                    setOriginalName(EMPTY_SLATE_VALUE);
                }
            } else {
                setName(EMPTY_SLATE_VALUE);
                setOriginalName(EMPTY_SLATE_VALUE);
            }
        } catch (error) {
            console.error('Ошибка при получении данных:', error);
            setName(EMPTY_SLATE_VALUE);
            setOriginalName(EMPTY_SLATE_VALUE);
        } finally {
            setLoading(false);
        }
    };

    const onEdit = async (): Promise<void> => {
        if (isSlateContentEmpty(name)) return;

        setLoading(true);
        try {
            const htmlContent = slateToHtml(name);
            await agent.UsefulInformationProvider.edit({
                name: htmlContent,
            });
            setOriginalName(name);
            setEditable(false);
            setSaved(true);
            setTimeout(() => setSaved(false), 3000);
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleBack = () => {
        setName(originalName);
        setEditable(false);
    };

    return (
        <div>
            {saved && (
                <Message positive>
                    <Message.Header>Изменения сохранены</Message.Header>
                </Message>
            )}

            {!loading ? (
                <Form>
                    <Grid>
                        <Grid.Column width={4}>
                            <label>
                                {getGeneralTitle(GeneralTitleCase.showUsefulInformationButtonBot)}
                                <i className="info circle icon grey"
                                   title="Для применения параметров форматирования необходимо: выделить часть текста и нажать Ctrl+B для выделения жирным, Ctrl+I для выделения курсивом, Ctrl+U для подчёркивания текста." />
                            </label>
                            <SlateEditableText
                                value={name}
                                onChange={(newValue) => {
                                    if (Array.isArray(newValue)) {
                                        setName(newValue);
                                    }
                                }}
                                maxLength={300}
                                disabled={hasRole(UserRole.OBSERVER) || !editable}
                            />
                            {isSlateContentEmpty(name) && <p style={{color: "red"}}>Поле не может быть пустым</p>}
                        </Grid.Column>

                        <Grid.Column width={3}>
                            <div className={styles.appointmentEditorButtons}>
                                {editable ? (
                                    <>
                                        <Button
                                            className={styles.button}
                                            color={'green'}
                                            type={'submit'}
                                            content={textLabels.saveButton}
                                            disabled={isSlateContentEmpty(name)}
                                            onClick={onEdit}
                                        />
                                        <Button
                                            className={styles.button}
                                            color={'grey'}
                                            type={'button'}
                                            content={textLabels.returnButton}
                                            onClick={handleBack}
                                        />
                                    </>
                                ) : (hasRole(UserRole.ADMIN) &&
                                    <Button
                                        className={styles.button}
                                        color={'teal'}
                                        type={'submit'}
                                        content={textLabels.editButton}
                                        onClick={() => setEditable(true)}
                                    />
                                )}
                            </div>
                        </Grid.Column>
                    </Grid>
                </Form>
            ) : (
                <Dimmer active inverted>
                    <Loader inverted>Загрузка конфигурации</Loader>
                </Dimmer>
            )}
        </div>
    );
}

export default UsefulInformationConfig;